import * as Sentry from '@sentry/browser';

import { ProductType } from '~/techstyle-shared/react-products';

/**
 * Generates a SKU without size information by extracting the base SKU from product data.
 * This function handles different product types and data formats, removing size/color suffixes
 * to get the core product identifier used for image URLs and other product references.
 *
 * @param {Object} options - Product data options for SKU generation
 * @param {string} [options.alias] - Product alias
 * @param {string} [options.itemNumber] - Product item number
 * @param {string} [options.item_number] - Alternative item number property name
 * @param {number} [options.productTypeId] - Product type ID for special handling (gift certificates, etc.)
 * @returns {string|null} The base SKU without size/color information, or null if insufficient data
 *
 * @description
 * Processing logic by product type:
 * - **Gift Certificates**: Returns first part of alias before space
 * - **Regular Products with itemNumber**: Removes last dash-separated segment (size)
 * - **Products with alias only**: Extracts SKU pattern (LETTERS+NUMBERS-NUMBERS) from beginning
 */
function generateSkuWithoutSize({
  alias,
  itemNumber,
  // eslint-disable-next-line camelcase
  item_number,
  productTypeId,
}) {
  // eslint-disable-next-line camelcase
  const itemNumberValue = item_number || itemNumber;
  // Some products like the membership will not have an item number!
  // (/account/wishlist) Product Sets need to use `product.alias` since they don’t have `bundleItems`
  if (!itemNumberValue && !alias) {
    Sentry.captureMessage(
      `Error: 'generateSkuWithoutSize' itemNumber nor alias provided`,
      'warning'
    );

    return null;
  }

  if (productTypeId === ProductType.GIFT_CERTIFICATE) {
    const splitAlias = alias.split(' ');
    if (splitAlias.length < 2) {
      Sentry.captureMessage(
        `Error: 'generateSkuWithoutSize' could not parse alias: ${alias}`,
        'warning'
      );

      return null;
    }
    return splitAlias[0];
  }

  // Parse off the last dash stuff...
  const itemArray = itemNumberValue && (itemNumberValue + '').split('-');

  let skuWithoutSize;
  if (itemNumberValue) {
    skuWithoutSize = itemArray.slice(0, -1).join('-');
  } else if (alias) {
    // For alias, extract SKU at the beginning
    const skuMatch = alias.match(/^[A-Z]+\d+-\d+/);
    skuWithoutSize = skuMatch ? skuMatch[0] : alias;
  }

  return skuWithoutSize;
}

export default generateSkuWithoutSize;
